import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { toast } from "sonner";
import { useSavedViewContextOptional } from "@/contexts/saved-view-context";
import { useSavedView } from "@/hooks/api/use-saved-views";
import { useAuth } from "@/hooks/auth/use-auth";
import { APIError } from "@/lib/api";
import {
  applyBrowserState,
  getOrgScopedItem,
  getPersonalDefaultViewId,
  resetOrgScopedKeysByPatterns,
  setPersonalDefaultViewId,
} from "@/lib/saved-view-utils";
import { isShareUrlRestoring } from "@/lib/share-url-utils";

interface UseSavedViewStateOptions {
  pageType: string;
}

export function useSavedViewState({ pageType }: UseSavedViewStateOptions) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const organizationId = user?.company?.default_organization?.id || "";

  // Use context for shared state (optional - may be null in test environments)
  const savedViewContext = useSavedViewContextOptional();
  const loadedViewId = savedViewContext?.loadedViewId ?? null;
  const hasUnsavedChanges = savedViewContext?.hasUnsavedChanges ?? false;
  const setLoadedView = savedViewContext?.setLoadedView ?? (() => {});
  const isLoadingView = savedViewContext?.isLoadingView ?? false;
  const setIsLoadingView = savedViewContext?.setIsLoadingView ?? (() => {});
  const clearChanges = savedViewContext?.clearChanges ?? (() => {});

  // Track if we've initialized this page load
  const hasInitializedRef = useRef(false);
  const hasAppliedViewRef = useRef(false);
  const isUserInitiatedChangeRef = useRef(false);

  // Personal default (from localStorage) - make it reactive
  const [personalDefaultId, setPersonalDefaultIdState] = useState<
    string | null
  >(() =>
    organizationId && pageType
      ? getPersonalDefaultViewId(pageType, organizationId)
      : null,
  );

  // Update personal default when organization/page changes
  useEffect(() => {
    if (organizationId && pageType) {
      const defaultId = getPersonalDefaultViewId(pageType, organizationId);
      setPersonalDefaultIdState(defaultId);
    }
  }, [organizationId, pageType]);

  // URL params
  const viewIdParam = searchParams.get("view");

  // Fetch data
  const {
    data: savedView,
    isLoading: isLoadingSavedView,
    error: savedViewError,
  } = useSavedView(viewIdParam || undefined);

  // Handle saved view errors
  useEffect(() => {
    if (savedViewError && viewIdParam && !isLoadingSavedView) {
      // Show error toast
      if (savedViewError instanceof APIError) {
        if (savedViewError.status === 404) {
          toast.error("Saved view not found", {
            description:
              "This saved view may have been deleted or you don't have permission to access it.",
          });
        } else if (savedViewError.status === 403) {
          toast.error("Access denied", {
            description: "You don't have permission to access this saved view.",
          });
        } else {
          toast.error("Failed to load saved view", {
            description:
              "An error occurred while loading the saved view. Please try again.",
          });
        }
      } else {
        toast.error("Failed to load saved view", {
          description:
            "An error occurred while loading the saved view. Please try again.",
        });
      }

      // Clear any stale loaded view to avoid re-inserting the invalid ID into the URL
      setLoadedView(null);

      // Clear the view param from URL
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("view");
      setSearchParams(newParams, { replace: true });
    }
  }, [
    savedViewError,
    viewIdParam,
    isLoadingSavedView,
    setSearchParams,
    searchParams, // Clear any stale loaded view to avoid re-inserting the invalid ID into the URL
    setLoadedView,
  ]);

  // Post-switch URL sync: if we have a loaded view but the URL lacks ?view, insert it
  useEffect(() => {
    if (loadedViewId && !viewIdParam && !isLoadingSavedView && !isLoadingView) {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set("view", loadedViewId);
        return newParams;
      });
    }
  }, [
    loadedViewId,
    viewIdParam,
    isLoadingSavedView,
    isLoadingView,
    setSearchParams,
  ]);

  // Apply view when it loads
  useEffect(() => {
    if (
      savedView &&
      viewIdParam &&
      !isLoadingSavedView &&
      !hasAppliedViewRef.current
    ) {
      hasAppliedViewRef.current = true;

      // Check if we have unsaved changes and this is NOT a user-initiated change
      // If user explicitly selected a view, always apply it
      if (hasUnsavedChanges && !isUserInitiatedChangeRef.current) {
        // Just set the loaded view ID so we know what view the changes are based on
        setLoadedView(savedView.id);
        // Don't apply the saved state - keep the existing unsaved changes
      } else {
        setIsLoadingView(true);

        // Apply the state in next tick to avoid race conditions
        setTimeout(() => {
          applyBrowserState(
            savedView.state_data,
            organizationId,
            savedView.page_type,
          );
          setLoadedView(savedView.id);
          // Clear unsaved changes after successful application (for user-initiated changes)
          if (isUserInitiatedChangeRef.current) {
            clearChanges();
          }
          const onApplied = () => {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => setIsLoadingView(false));
            });
            window.removeEventListener("browser-state-applied", onApplied);
          };
          window.addEventListener("browser-state-applied", onApplied, {
            once: true,
          });
        }, 0);
      }

      // Reset the flag after processing
      isUserInitiatedChangeRef.current = false;
    }
  }, [
    savedView,
    viewIdParam,
    isLoadingSavedView,
    organizationId,
    setIsLoadingView,
    setLoadedView,
    hasUnsavedChanges,
    clearChanges,
  ]);

  // Note: One-time shares are now handled entirely by ShareUrlHandler
  // This hook no longer needs to apply share data

  // Initial load - check for last used, personal default, or system default
  useEffect(() => {
    if (!organizationId || !pageType) return;
    if (hasInitializedRef.current) return;

    // If we have a URL param, let the above effects handle it
    if (viewIdParam) {
      hasInitializedRef.current = true;
      return;
    }

    // Skip default empty-state initialization during share URL restoration in a fresh tab
    const isOneTimeRestoreActive = organizationId
      ? Boolean(
          getOrgScopedItem(
            organizationId,
            "sessionStorage",
            "saved-views::one-time-restore",
          ),
        )
      : false;
    if (isShareUrlRestoring() || isOneTimeRestoreActive) {
      hasInitializedRef.current = true;
      return;
    }

    hasInitializedRef.current = true;

    // Check if we have unsaved changes from a previous session
    if (hasUnsavedChanges) {
      // Don't apply any view state - just keep what's in storage
      // The loadedViewId should already be set from before navigation
      return;
    }

    // Check for previously loaded view first (persisted in sessionStorage)
    if (loadedViewId) {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set("view", loadedViewId);
        return newParams;
      });
      return;
    }

    // Check for personal default
    if (personalDefaultId) {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set("view", personalDefaultId);
        return newParams;
      });
    } else {
      // System default - apply empty state
      setIsLoadingView(true);

      // Apply the empty state first
      const emptyState = {
        localStorage: {},
        sessionStorage: {},
        url: window.location.pathname,
        page: pageType,
      };
      applyBrowserState(emptyState, organizationId, pageType);
      setLoadedView(null);

      // Clear loading after we observe browser-state-applied
      const onApplied = () => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => setIsLoadingView(false));
        });
        window.removeEventListener("browser-state-applied", onApplied);
      };
      window.addEventListener("browser-state-applied", onApplied, {
        once: true,
      });
    }
  }, [
    organizationId,
    pageType,
    viewIdParam,
    loadedViewId,
    personalDefaultId,
    setSearchParams,
    setLoadedView,
    setIsLoadingView,
    hasUnsavedChanges,
  ]);

  // Consistent loadView function
  const loadView = useCallback(
    (viewId: string | null) => {
      // Mark this as a user-initiated change
      isUserInitiatedChangeRef.current = true;
      if (viewId) {
        // Check if we're reloading the same view (reset functionality)
        const isReloadingSameView = viewIdParam === viewId;

        if (isReloadingSameView && savedView) {
          // Directly re-apply the current view's state
          setIsLoadingView(true);

          setTimeout(() => {
            applyBrowserState(
              savedView.state_data,
              organizationId,
              savedView.page_type,
            );
            setLoadedView(savedView.id);
            clearChanges(); // Clear any unsaved changes after re-applying
            const onApplied = () => {
              requestAnimationFrame(() => {
                requestAnimationFrame(() => setIsLoadingView(false));
              });
              window.removeEventListener("browser-state-applied", onApplied);
            };
            window.addEventListener("browser-state-applied", onApplied, {
              once: true,
            });
          }, 0);
        } else {
          // Load a different saved view via URL change
          hasAppliedViewRef.current = false;
          // Don't clear changes here - let them be cleared after successful application
          setSearchParams((prev) => {
            const newParams = new URLSearchParams(prev);
            newParams.set("view", viewId);
            return newParams;
          });
        }
      } else {
        // System default - always apply (for reset functionality)
        setIsLoadingView(true);

        // Apply the empty state first
        const emptyState = {
          localStorage: {},
          sessionStorage: {},
          url: window.location.pathname,
          page: pageType,
        };
        applyBrowserState(emptyState, organizationId, pageType);
        setLoadedView(null);

        // Give components time to react to the cleared state before marking as done
        // This prevents their default value updates from being tracked as changes
        const onApplied = () => {
          requestAnimationFrame(() => {
            clearChanges(); // Clear any unsaved changes after state is applied
            requestAnimationFrame(() => setIsLoadingView(false));
          });
          window.removeEventListener("browser-state-applied", onApplied);
        };
        window.addEventListener("browser-state-applied", onApplied, {
          once: true,
        });

        // Remove URL param if present
        if (viewIdParam) {
          setSearchParams((prev) => {
            const newParams = new URLSearchParams(prev);
            newParams.delete("view");
            return newParams;
          });
        }
      }
    },
    [
      pageType,
      organizationId,
      viewIdParam,
      savedView,
      setSearchParams,
      setIsLoadingView,
      setLoadedView,
      clearChanges,
    ],
  );

  // Simple function to set personal default
  const setPersonalDefault = useCallback(
    (viewId: string | null) => {
      if (organizationId && pageType) {
        setPersonalDefaultViewId(pageType, viewId, organizationId);
        setPersonalDefaultIdState(viewId);
      }
    },
    [organizationId, pageType],
  );

  // Reset to current view's saved configuration
  const resetToCurrentView = useCallback(() => {
    setIsLoadingView(true);

    if (loadedViewId && savedView) {
      // Reset to the saved view's configuration
      setTimeout(() => {
        applyBrowserState(
          savedView.state_data,
          organizationId,
          savedView.page_type,
        );
        clearChanges();
        const onApplied = () => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => setIsLoadingView(false));
          });
          window.removeEventListener("browser-state-applied", onApplied);
        };
        window.addEventListener("browser-state-applied", onApplied, {
          once: true,
        });
      }, 0);
    } else {
      // Reset to system default (empty state)
      const emptyState = {
        localStorage: {},
        sessionStorage: {},
        url: window.location.pathname,
        page: pageType,
      };
      applyBrowserState(emptyState, organizationId, pageType);

      const onApplied = () => {
        requestAnimationFrame(() => {
          clearChanges();
          requestAnimationFrame(() => setIsLoadingView(false));
        });
        window.removeEventListener("browser-state-applied", onApplied);
      };
      window.addEventListener("browser-state-applied", onApplied, {
        once: true,
      });
    }
  }, [
    loadedViewId,
    savedView,
    organizationId,
    pageType,
    setIsLoadingView,
    clearChanges,
  ]);

  // Reset specific storage keys to current view's saved configuration
  const resetSpecificKeys = useCallback(
    (keyPatterns: string[]) => {
      if (!organizationId) return;

      // Set loading flag to prevent change detection during reset
      // Pass true to preserve the hasUnsavedChanges flag for individual resets
      setIsLoadingView(true, true);

      // Get the state to reset to
      let stateToApply: Record<string, unknown>;

      if (loadedViewId && savedView) {
        // Use the saved view's configuration
        stateToApply = savedView.state_data;
      } else {
        // Use system default (empty state)
        stateToApply = {
          localStorage: {},
          sessionStorage: {},
          url: window.location.pathname,
          page: pageType,
        };
      }

      // Use centralized helper to avoid direct storage access
      resetOrgScopedKeysByPatterns(
        organizationId,
        "localStorage",
        keyPatterns,
        (stateToApply.localStorage as Record<string, unknown>) || {},
      );
      resetOrgScopedKeysByPatterns(
        organizationId,
        "sessionStorage",
        keyPatterns,
        (stateToApply.sessionStorage as Record<string, unknown>) || {},
      );

      // Clear loading flag when we observe the browser-state-applied signal
      // Pass true to preserve the hasUnsavedChanges flag
      const onApplied = () => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => setIsLoadingView(false, true));
        });
        window.removeEventListener("browser-state-applied", onApplied);
      };
      window.addEventListener("browser-state-applied", onApplied, {
        once: true,
      });
    },
    [loadedViewId, savedView, organizationId, pageType, setIsLoadingView],
  );

  return {
    currentViewId: loadedViewId,
    hasChanges: hasUnsavedChanges,
    isLoadingView,
    loadView,
    clearView: () => loadView(null),
    clearChanges,
    resetToCurrentView,
    resetSpecificKeys,
    personalDefaultId,
    setPersonalDefault,
  };
}
